import { Injectable } from '@angular/core';
import { BaseViewModel } from '../../../../../../../../models/base/base-view-model';
import { BehaviorSubject, Observable } from 'rxjs';
import { TheWoodsCardData } from '../../../../../../../../models/print-cards/the-woods-card-data';
import { map, shareReplay } from 'rxjs/operators';
import { VariantType } from '../../../../../../../../models/enum/dto/variant-type.enum';
import { ProductType } from '../../../../../../../../models/enum/dto/product-type.enum';
import { memoize } from '../../../../../../../shared/decorators/memoize';
import { MeasurementUtils } from '../../../../../../../../utils/measurement-utils';
import { NumberUtils } from '../../../../../../../../utils/number.utils';

@Injectable()
export class TheWoodsPotencyMeterViewModel extends BaseViewModel {
  private readonly _cardData = new BehaviorSubject<TheWoodsCardData>(null);
  public readonly cardData$ = this._cardData as Observable<TheWoodsCardData>;
  connectToCardData(cardData: TheWoodsCardData) {
    this._cardData.next(cardData);
  }

  public readonly potencyScoreColor$ = this.cardData$.pipe(
    map(cardData => cardData?.getStrainTypeColor()),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly potencyScore$ = this.cardData$.pipe(
    map(cardData => this.getPotencyScore(cardData)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly iterablePotencyScore$ = this.potencyScore$.pipe(
    map(score => (score ? Array.from({ length: score }, (_, i) => i + 1) : [])),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /**
   * Fallback Rule - If a product type doesn’t match any category above, assign 5
   */
  private getPotencyScore(cardData: TheWoodsCardData): number {
    const productType = cardData?.rowProductType();
    const variantType = cardData?.rowVariantType();
    if (this.isCbdFocusedProduct(cardData)) {
      return this.getCbdFocusedPotencyScore(cardData);
    }
    if (variantType === VariantType.PreRoll) {
      return this.getFlowerOrPreRollPotencyScore(cardData);
    }
    if (variantType === VariantType.Capsule) {
      return this.getEdibleDrinkOrCapsulePotencyScore(cardData);
    }
    if (productType === ProductType.Flower) {
      return this.getFlowerOrPreRollPotencyScore(cardData);
    }
    if (productType === ProductType.Concentrate || productType === ProductType.Vape) {
      return this.getConcentrateOrVapePotencyScore(cardData);
    }
    if (productType === ProductType.Edible || productType === ProductType.Beverage) {
      return this.getEdibleDrinkOrCapsulePotencyScore(cardData);
    }
    if (productType === ProductType.Wellness || productType === ProductType.Oil) {
      return this.getWellnessOrOilPotencyScore(cardData);
    }
    return 5;
  }

  private isCbdFocusedProduct(cardData: TheWoodsCardData): boolean {
    if (cardData?.isAccessory()) return false;
    return this.getMaxCbd(cardData) > this.getMaxThc(cardData);
  }

  /**
   * Override: CBD-Focused Products
   * (CBD > THC) → apply mg-based thresholds
   *   5 = CBD ≥ 30mg (per ml or unit)
   *   4 = CBD ≥ 15mg
   *   3 = CBD > 0mg
   */
  private getCbdFocusedPotencyScore(cardData: TheWoodsCardData): number {
    const cbd = this.getMaxCbd(cardData);
    const variant = cardData?.getScopedVisibleVariants()?.firstOrNull();
    const valueForComparison = MeasurementUtils.convert(variant?.cannabisUnitOfMeasure, 'mg', variant?.unitSize, cbd);
    if (valueForComparison >= 30) {
      return 5;
    } else if (valueForComparison >= 15) {
      return 4;
    } else if (valueForComparison > 0) {
      return 3;
    }
    return 0;
  }

  /**
   * Flower & Pre-Roll
   *   5 = THC ≥ 25%
   *   4 = THC ≥ 17%
   *   3 = THC > 0%
   */
  private getFlowerOrPreRollPotencyScore(cardData: TheWoodsCardData): number {
    const thc = this.getMaxThc(cardData);
    const variant = cardData?.getScopedVisibleVariants()?.firstOrNull();
    const valueForComparison = MeasurementUtils.convert(variant?.cannabisUnitOfMeasure, '%', variant?.unitSize, thc);
    if (valueForComparison >= 25) {
      return 5;
    } else if (valueForComparison >= 17) {
      return 4;
    } else if (valueForComparison > 0) {
      return 3;
    }
    return 0;
  }

  /**
   * Concentrates & Vapes
   *   5 = THC ≥ 85%
   *   4 = THC ≥ 70%
   *   3 = THC > 0%
   */
  private getConcentrateOrVapePotencyScore(cardData: TheWoodsCardData): number {
    const thc = this.getMaxThc(cardData);
    const variant = cardData?.getScopedVisibleVariants()?.firstOrNull();
    const valueForComparison = MeasurementUtils.convert(variant?.cannabisUnitOfMeasure, '%', variant?.unitSize, thc);
    if (valueForComparison >= 85) {
      return 5;
    } else if (valueForComparison >= 70) {
      return 4;
    } else if (valueForComparison > 0) {
      return 3;
    }
    return 0;
  }

  /**
   * Edibles, Drinks, Capsules
   *   5 = THC ≥ 6mg (per serving)
   *   4 = THC ≥ 5mg
   *   3 = THC > 0mg
   */
  private getEdibleDrinkOrCapsulePotencyScore(cardData: TheWoodsCardData): number {
    const variant = cardData?.getScopedVisibleVariants()?.firstOrNull();
    const thc = this.getMaxThc(cardData) ?? 0;
    const convertedThc = MeasurementUtils.convert(variant?.cannabisUnitOfMeasure, 'mg', variant?.unitSize, thc);
    const packageQuantity = cardData?.getMaxPackageQuantity() || 1;
    const valueForComparison = NumberUtils.roundToTwoDecimalPlaces(convertedThc / packageQuantity);
    if (valueForComparison >= 6) {
      return 5;
    } else if (valueForComparison >= 5) {
      return 4;
    } else if (valueForComparison > 0) {
      return 3;
    }
    return 0;
  }

  /**
   * Wellness / Oils
   *   5 = THC ≥ 30mg (per ml or unit)
   *   4 = THC ≥ 15mg
   *   3 = THC > 0mg
   */
  private getWellnessOrOilPotencyScore(cardData: TheWoodsCardData): number {
    const thc = this.getMaxThc(cardData);
    const variant = cardData?.getScopedVisibleVariants()?.firstOrNull();
    const valueForComparison = MeasurementUtils.convert(variant?.cannabisUnitOfMeasure, 'mg', variant?.unitSize, thc);
    if (valueForComparison >= 30) {
      return 5;
    } else if (valueForComparison >= 15) {
      return 4;
    } else if (valueForComparison > 0) {
      return 3;
    }
    return 0;
  }

  @memoize
  private getMaxThc(cardData: TheWoodsCardData): number {
    return cardData?.getMaxRowCannabinoid('THC') ?? 0;
  }

  @memoize
  private getMaxCbd(cardData: TheWoodsCardData): number {
    return cardData?.getMaxRowCannabinoid('CBD') ?? 0;
  }
}
